import React from "react";
import type {MerchantMovementItem} from "../../api";
import {
  formatMerchantDate,
  merchantMovementLabel,
  merchantOrdersHashForRemoteId,
} from "../../utils";

interface MerchantMovementsListProps {
  items: MerchantMovementItem[];
  emptyMessage: string;
}

function deltaTone(delta: string): string {
  return delta.trim().startsWith("-") ? "negative" : "positive";
}

// Два времени строки. «Записано» (createdAt) — момент записи в книгу, по нему
// идёт список, потому что «Баланс после» это нарастающий итог, снятый в этот
// самый момент. «Время площадки» (occurredAt) — когда событие случилось у
// источника: строки внешнего источника записываются пачкой и позже, поэтому у
// них времена расходятся на часы, и без второй колонки выписка выглядит
// перепутанной, хотя она верна.
//
// Совпали с точностью до секунды — второе время не показываем: у всего, что
// рождается внутри системы, оно совпадает всегда, и повторять его рядом значит
// зашуметь выписку.
function platformTime(movement: MerchantMovementItem): string {
  if (sameToTheSecond(movement.createdAt, movement.occurredAt)) return "";
  return formatMerchantDate(movement.occurredAt);
}

// Секунда, а не отформатированная минута: строки одной пачки разведены
// микросекундами, и на минутах пачка выглядела бы одинаковой сама с собой.
function sameToTheSecond(left: string, right: string): boolean {
  if (left === right) return true;
  const a = new Date(left).getTime();
  const b = new Date(right).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return false;
  return Math.floor(a / 1000) === Math.floor(b / 1000);
}

function OrderReference({remoteId}: {remoteId?: string}): JSX.Element {
  if (!remoteId) return <span className="muted">—</span>;
  return (
    <a className="mono merchant-reference-link" href={merchantOrdersHashForRemoteId(remoteId)}>
      {remoteId}
    </a>
  );
}

export function MerchantMovementsList({
  items,
  emptyMessage,
}: MerchantMovementsListProps): JSX.Element {
  if (items.length === 0) {
    return <div className="empty-state compact">{emptyMessage}</div>;
  }

  return (
    <>
      <div className="table-wrapper merchant-movements-table-wrap">
        <table className="merchant-movements-table">
          <thead>
          <tr>
            <th>Записано</th>
            <th>Время площадки</th>
            <th>Причина</th>
            <th>Изменение</th>
            <th>Баланс после</th>
            <th>Заявка</th>
          </tr>
          </thead>
          <tbody>
          {items.map((movement) => (
            <tr key={movement.id}>
              <td className="mono">{formatMerchantDate(movement.createdAt)}</td>
              <td className="mono muted">{platformTime(movement)}</td>
              <td>{merchantMovementLabel(movement.kind)}</td>
              <td className={`mono balance-delta ${deltaTone(movement.delta)}`}>
                {movement.delta} {movement.currency}
              </td>
              <td className="mono">
                {movement.balanceAfter === null
                  ? "—"
                  : `${movement.balanceAfter} ${movement.currency}`}
              </td>
              <td><OrderReference remoteId={movement.remoteId}/></td>
            </tr>
          ))}
          </tbody>
        </table>
      </div>

      <div className="merchant-movement-cards">
        {items.map((movement) => (
          <article className="merchant-movement-card" key={movement.id}>
            <div className="merchant-movement-card-head">
              <strong>{merchantMovementLabel(movement.kind)}</strong>
              <span className={`mono balance-delta ${deltaTone(movement.delta)}`}>
                {movement.delta} {movement.currency}
              </span>
            </div>
            <dl className="merchant-movement-card-grid">
              <div>
                <dt>Записано</dt>
                <dd>{formatMerchantDate(movement.createdAt)}</dd>
              </div>
              {platformTime(movement) && (
                <div>
                  <dt>Время площадки</dt>
                  <dd>{platformTime(movement)}</dd>
                </div>
              )}
              <div>
                <dt>Баланс после</dt>
                <dd className="mono">
                  {movement.balanceAfter === null
                    ? "—"
                    : `${movement.balanceAfter} ${movement.currency}`}
                </dd>
              </div>
              <div>
                <dt>Заявка</dt>
                <dd><OrderReference remoteId={movement.remoteId}/></dd>
              </div>
            </dl>
          </article>
        ))}
      </div>
    </>
  );
}
